/**
 * chatFeatureAccess — may this USER use a chat feature here? The role-based (RBAC)
 * half of the chat's permission vocabulary.
 *
 * Three questions, three files, never mixed:
 * - [canSendMessage](./canSendMessage.ts): can this message go right now? (composer state)
 * - [chatParticipation](./chatParticipation.ts): may this user take part in THIS chat? (ownership)
 * - this file: does the user's ROLE grant this feature in this knowledge context?
 *
 * Pure data and pure functions. The grant lookup arrives as an `isGranted` function
 * (built once by `hooks/useChatFeatureAccess`), so the registries and the send-time
 * payload builders evaluate permissions without touching React or the store.
 *
 * A feature names its permission declaratively — a single key, or one key per
 * knowledge type — and `isFeatureAllowed` is the only place that reads it. A feature
 * with no key for the current knowledge type is ungated there (an assistant chat's
 * tools, for instance, are decided by the assistant's own configuration).
 */

import { KNOWLEDGE_TYPE } from 'src/content/ChatV2/enums';
import { FEATURE_PERMISSIONS } from 'src/types/enum';

/** Resolved grant lookup: `true` when the user's role grants `key`. */
export type IsGranted = (key: FEATURE_PERMISSIONS) => boolean;

/** The knowledge types a user can switch between. */
export type SwitchableKnowledge =
  | KNOWLEDGE_TYPE.ENTERPRISE
  | KNOWLEDGE_TYPE.WORLD;

/** How a feature declares the permission it needs. */
export type FeaturePermissionSpec =
  | FEATURE_PERMISSIONS
  | Partial<Record<KNOWLEDGE_TYPE, FEATURE_PERMISSIONS>>;

/** The key `spec` requires under `knowledge`, or `undefined` when ungated there. */
export function permissionFor(
  spec: FeaturePermissionSpec | undefined,
  knowledge: KNOWLEDGE_TYPE
): FEATURE_PERMISSIONS | undefined {
  if (typeof spec === 'object') {
    return spec[knowledge];
  }
  return spec;
}

/** Whether the feature declared by `spec` is allowed under `knowledge`. */
export function isFeatureAllowed(
  spec: FeaturePermissionSpec | undefined,
  knowledge: KNOWLEDGE_TYPE,
  isGranted: IsGranted
): boolean {
  const key = permissionFor(spec, knowledge);
  return key === undefined || isGranted(key);
}

/** Permission behind each switchable knowledge type. */
export const KNOWLEDGE_PERMISSION: Record<
  SwitchableKnowledge,
  FEATURE_PERMISSIONS
> = {
  [KNOWLEDGE_TYPE.ENTERPRISE]: FEATURE_PERMISSIONS.CHAT_ENTERPRISE_KNOWLEDGE,
  [KNOWLEDGE_TYPE.WORLD]: FEATURE_PERMISSIONS.CHAT_WORLD_KNOWLEDGE
};

/**
 * Fallback order when the requested knowledge type is not allowed. Enterprise first,
 * matching v1's default for a user who has it.
 */
const KNOWLEDGE_FALLBACK_ORDER: readonly SwitchableKnowledge[] = [
  KNOWLEDGE_TYPE.ENTERPRISE,
  KNOWLEDGE_TYPE.WORLD
];

/** The switchable knowledge types this user's role allows, in fallback order. */
export function allowedKnowledgeTypes(
  isGranted: IsGranted
): SwitchableKnowledge[] {
  return KNOWLEDGE_FALLBACK_ORDER.filter((type) =>
    isGranted(KNOWLEDGE_PERMISSION[type])
  );
}

/**
 * `requested` when it is allowed, otherwise the first allowed switchable type.
 * Non-switchable types (an assistant chat) and a user allowed neither type pass
 * through unchanged, so the result is always something the surface can render.
 */
export function clampKnowledge(
  requested: KNOWLEDGE_TYPE,
  allowed: readonly SwitchableKnowledge[]
): KNOWLEDGE_TYPE {
  const switchable =
    requested === KNOWLEDGE_TYPE.ENTERPRISE ||
    requested === KNOWLEDGE_TYPE.WORLD;
  if (!switchable || allowed.length === 0) {
    return requested;
  }
  return (allowed as readonly KNOWLEDGE_TYPE[]).includes(requested)
    ? requested
    : allowed[0];
}

/**
 * Attachments permission for a surface that declares none (the plain conversation,
 * compare): it follows the turn's knowledge type. Surfaces with their own key
 * (assistant, project) set `composer.attachmentPermission` in their mode config.
 */
export const DEFAULT_ATTACHMENT_PERMISSION: FeaturePermissionSpec = {
  [KNOWLEDGE_TYPE.ENTERPRISE]:
    FEATURE_PERMISSIONS.CHAT_ENTERPRISE_KNOWLEDGE_ATTACHMENTS,
  [KNOWLEDGE_TYPE.WORLD]: FEATURE_PERMISSIONS.CHAT_WORLD_KNOWLEDGE_ATTACHMENTS
};
